import { Injectable } from '@angular/core';
import { ApiService } from './shared/api.service';
import { IContactEmailCampaignSubscriptions } from './shared/interfaces';
import { NotificationsService } from './shared/notifications.service';
import { NotificationMessageType } from './shared/enums';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATION_MAP } from './enums';

@Injectable({
    providedIn: 'root'
})
export class ProfileSubscriptionsService {
    data?: IContactEmailCampaignSubscriptions;

    constructor (
        private apiSvc: ApiService,
        private notificationsSvc: NotificationsService,
        private translateSvc: TranslateService,
    ) {}

    async submit() {
        await this.apiSvc.setContactEmailCampaignSubscriptions(this.getDataFromHtml());
        this.notificationsSvc.showNotification(NotificationMessageType.success, this.translateSvc.instant(TRANSLATION_MAP.SAVED));
    }

    async loadSubscriptionsItemElements() {
        const sectionEl = this.getSectionElement();
        if (!sectionEl) {
            return;
        }

        this.data = await this.apiSvc.getContactEmailCampaignSubscriptions();
        let itemsContainerEl = this.getSubscriptionItemsContainerElement();
        if (!itemsContainerEl) {
            const currentContent = sectionEl.querySelector('.aza-section-content')?.innerHTML;
            sectionEl.innerHTML = `
            ${mockSubscriptionHtml}
            `;
            sectionEl.querySelector('[aza-subscriptions-profile-section-content]').innerHTML = `
            ${currentContent}
            ${sectionEl.querySelector('[aza-subscriptions-profile-section-content]').innerHTML}
            `;
            itemsContainerEl = this.getSubscriptionItemsContainerElement();
        }
        await this.loadStaticHtml(itemsContainerEl);
    }

    private getSubscriptionItemsContainerElement() {
        return document.querySelector('[aza-field-subscriptions-items-container]');
    }

    private async loadStaticHtml(itemsContainerEl: Element) {
        itemsContainerEl.querySelector('select')?.remove();
        this.data.subscriptions.forEach(({ subscribed, email_campaign_type_name }) => {
            const itemElement = document.createElement('div');
            itemElement.innerHTML = `
            <div class="aza-field-content aza-field-type-boolean">
                <input type="checkbox" class="auto-width profile-subscriptions-unsubscribe-input-field"
                    name="profile-subscriptions-items-input" value="${email_campaign_type_name}" ${subscribed ? 'checked' : ''}>
                <span class="profile-subscriptions-items-text">${email_campaign_type_name}</span>
            </div>
            `.trim();
            itemsContainerEl.prepend(itemElement);
        });
        const submitButton = document.querySelector<HTMLButtonElement>('[aza-form-subscription-submit-button-section].section-form-button button');
        if (submitButton) {
            // it should use `onclick` instead of addEventListener
            // in order to override the `onclick` implementation in app.component.ts
            // for attaching the registration event
            submitButton.onclick = async () => {
                submitButton.disabled = true;
                await this.submit().finally(() => submitButton.disabled = false);
            };
        }

        const unsubscribeCheckbox = this.getInputUnsubscribeElement();
        if (unsubscribeCheckbox) {
            unsubscribeCheckbox.addEventListener('input', ({ target }) => {
                this.toggleShowSubscriptionItemsElement(!(target as HTMLInputElement).checked);
            });
            unsubscribeCheckbox.checked = this.data?.unsubscribe;
        }
        this.toggleShowSubscriptionItemsElement(!this.data?.unsubscribe);
    }

    private toggleShowSubscriptionItemsElement(show: boolean) {
        const subscriptionsItemsElements = this.getSectionElement()?.querySelector<HTMLDivElement>('[aza-field-subscriptions-items]');
        if (subscriptionsItemsElements?.parentElement) {
            subscriptionsItemsElements.parentElement.style.display = show ? 'block' : 'none';
        }
    }

    getDataFromHtml(): IContactEmailCampaignSubscriptions {
        const itemsContainerEl = this.getSubscriptionItemsContainerElement();
        return {
            subscriptions: this.data?.subscriptions?.map(item => ({
                ...item,
                subscribed: (itemsContainerEl?.querySelector(`[name="profile-subscriptions-items-input"][value="${item.email_campaign_type_name}"]`) as HTMLInputElement)?.checked,
            })),
            unsubscribe: !!(this.getInputUnsubscribeElement())?.checked
        };
    }

    private getInputUnsubscribeElement() {
        return document.querySelector<HTMLInputElement>('[name="profile-subscriptions-unsubscribe-input-field"]');
    }

    private getSectionElement() {
        return document.querySelector('[aza-subscriptions-profile]');
    }
}

const mockSubscriptionHtml = `
<div aza-block-container="" aza-dependency="" aza-alternate="" class="section-form-single-column aza-section aza-theme aza-form">
  <div aza-subscriptions-profile-section-content class="aza-subscriptions-profile-section-content aza-section-content aza-section-colors">
    <div class="aza-form-field-area">
      <div aza-block-container="" aza-dependency="" class="aza-input-field">
        <div class="aza-field-content aza-field-type-boolean">
          <input type="checkbox" name="profile-subscriptions-unsubscribe-input-field" class="auto-width profile-subscriptions-unsubscribe-input-field">
          <span data-azavista-translatable="TRANSLATION_KEY_6565f3bb6b8b6070c00d9a63" class="pofile-subscriptions-unsubscribe-text"> Do not send any email </span>
        </div>
      </div>
      <div aza-block-container="" aza-dependency="" class="aza-input-field">
        <div aza-field-subscriptions-items class="aza-field-content aza-field-type-multi-select aza-field-subscriptions-items">
          <div>
            <label data-azavista-translatable="TRANSLATION_KEY_6565f3b36b8b6070c00d9a62" class="aza-text"> Subscriptions <span></span>
            </label>
          </div>
          <div aza-field-subscriptions-items-container class="aza-field-subscriptions-items-container">
          </div>
          <div id="6565f25387751c00a12b22fa-field-validation-errors" class="aza-field-validation-error"></div>
        </div>
      </div>
    </div>
  </div>
</div>
<section aza-dependency="" aza-alternate="" aza-form-subscription-submit-button-section="" class="section-form-button aza-section aza-theme section-form-button-exception">
  <div class="aza-section-content aza-section-colors">
    <button type="submit" class="aza-button aza-section-inverted-colors">
      <span data-azavista-translatable="TRANSLATION_KEY_6">SUBMIT</span>
    </button>
  </div>
</section>
`;
